import CopyrightIcon from "@mui/icons-material/Copyright";
import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Dialog, DialogContent, Grow } from "@mui/material";
import { motion, AnimatePresence, useAnimationFrame } from "framer-motion";
import { useReducedMotion } from "./ui/useReducedMotion";
import { ThemeScope, Button, Input, Label, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, Badge, Separator } from "./ui/shadcn";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../context/AuthContext";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import TabletIcon from "@mui/icons-material/Tablet";
import DevicesIcon from "@mui/icons-material/Devices";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const MotionButton = motion.create(Button);
const MotionCard = motion.create(Card);

// Drifting background blob: framer-motion's useAnimationFrame writes the transform straight to the
// element (compositor-only: translate3d + scale), so it keeps moving smoothly.
function Blob({ ax, ay, amp, dur, style }) {
  const ref = useRef(null);
  useAnimationFrame((t) => {
    const el = ref.current;
    if (!el) return;
    const p = (t / 1000 / dur) * Math.PI * 2;
    const x = (Math.sin(p) * ax * window.innerWidth) / 100;
    const y = (Math.cos(p * 0.8) * ay * window.innerHeight) / 100;
    const sc = 1 + Math.sin(p * 0.6) * amp;
    el.style.transform = "translate3d(" + x.toFixed(1) + "px," + y.toFixed(1) + "px,0) scale(" + sc.toFixed(3) + ")";
  });
  return <div ref={ref} style={{ ...style, willChange: "transform" }} />;
}

function LoginDesign02({ connectionString }) {
  const reduceMotion = useReducedMotion();
  const path = connectionString;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { login } = useAuth();

  const autoIdentifier = searchParams.get("identifier");
  const autoUsername = searchParams.get("UserName");
  const autoMobile = searchParams.get("mobile");
  const autoEmail = searchParams.get("email");
  const autoPassword = searchParams.get("password");
  const redirectPathFromQuery = searchParams.get("redirect");

  const [identifier, setIdentifier] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCompanySelectionVisible, setIsCompanySelectionVisible] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // Session limit picker
  const [sessionPickerData, setSessionPickerData] = useState(null);
  const [kickingSessionId, setKickingSessionId] = useState(null);

  // Device fingerprint (created once, persisted in localStorage)
  const getDeviceId = () => {
    let did = localStorage.getItem("_did");
    if (!did) {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        did = window.crypto.randomUUID();
      } else {
        // Fallback for HTTP (non-localhost) — crypto.randomUUID requires HTTPS
        did = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
      }
      localStorage.setItem("_did", did);
    }
    return did;
  };

  const getBrowserName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Chrome") && !ua.includes("Edg")) return `Chrome on ${getOSName()}`;
    if (ua.includes("Firefox")) return `Firefox on ${getOSName()}`;
    if (ua.includes("Safari") && !ua.includes("Chrome")) return `Safari on ${getOSName()}`;
    if (ua.includes("Edg")) return `Edge on ${getOSName()}`;
    return `Browser on ${getOSName()}`;
  };

  const getOSName = () => {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    return "Unknown OS";
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (mins < 1)   return "just now";
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const deviceIcon = (type) => {
    if (type === "mobile")  return <PhoneAndroidIcon sx={{ fontSize: 20, color: "#a5b4fc" }} />;
    if (type === "tablet")  return <TabletIcon sx={{ fontSize: 20, color: "#a5b4fc" }} />;
    if (type === "desktop") return <DesktopWindowsIcon sx={{ fontSize: 20, color: "#a5b4fc" }} />;
    return <LaptopIcon sx={{ fontSize: 20, color: "#a5b4fc" }} />;
  };

  const handleTogglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s?.trim());

  useEffect(() => {
    const handleMessage = (ev) => {
      try {
        const data = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        if (!data || !data.type) return;
        if (data.type === "BIOMETRIC_LOGIN") {
          FN_HandleLogin(data.identifier, data.password);
        }
        if (data.type === "BIOMETRIC_FAILED") {
          setErrorMessage("Biometric authentication failed or cancelled.");
        }
        if (data.type === "BIOMETRIC_NOT_AVAILABLE") {
          setErrorMessage("Biometrics not available on this device.");
        }
      } catch (err) {
        console.warn("Error parsing message from native:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
    };
  }, []);

  const isLikelyMobile = (s) => {
    if (!s) return false;
    const cleaned = s.replace(/[\s\-\(\)\+]/g, "");
    const digits = cleaned.replace(/\D/g, "");
    const nonDigitCount = cleaned.replace(/\d/g, "").length;
    return (digits.length >= 6 && digits.length <= 15 && nonDigitCount === 0);
  };

  const determineIdentifierType = (id) => {
    const trimmed = id?.trim() || "";
    if (isEmail(trimmed)) return "email";
    if (isLikelyMobile(trimmed)) return "mobile";
    return "UserName";
  };

  useEffect(() => {
    const ai = autoIdentifier || autoUsername || autoMobile || autoEmail;
    if (ai && autoPassword) {
      setIsAutoLogin(true);
      setIdentifier(ai);
      setUserPassword(autoPassword);
      FN_HandleLogin(ai, autoPassword);
    }
  }, [autoIdentifier, autoUsername, autoMobile, autoEmail, autoPassword]);

  const FN_HandleLogin = async (overrideIdentifier, overridePassword, kickSessionId = null) => {
    setErrorMessage("");
    setIsLoading(true);
    const id = (overrideIdentifier ?? identifier ?? "").trim();
    const pwd = overridePassword ?? userPassword;

    if (!id || !pwd) {
      setErrorMessage("Please provide identifier and password.");
      setIsLoading(false);
      return;
    }

    try {
      const identifierType = determineIdentifierType(id);

      // Build device info
      let deviceInfo = {
        deviceId:   getDeviceId(),
        deviceName: getBrowserName(),
        platform:   navigator.platform,
        deviceType: "web",
      };
      if (window.electronAPI) {
        try { deviceInfo = await window.electronAPI.getDeviceInfo(); } catch (_) {}
      }

      const payload = {
        identifier: id,
        password: pwd,
        type: identifierType,
        deviceId:     deviceInfo.deviceId,
        deviceName:   deviceInfo.deviceName,
        platform:     deviceInfo.platform,
        deviceType:   deviceInfo.deviceType,
        kickSessionId: kickSessionId,
      };
      const response = await axios.post(`${path}/api/login`, payload,{ withCredentials: true });

      // Session limit reached — show device picker
      if (response.data.sessionLimitReached) {
        setSessionPickerData(response.data);
        setIsLoading(false);
        return;
      }

      if (!response.data.success) {
        setErrorMessage(response.data.message || "Invalid credentials.");
        return;
      }

      // Store session token for all future API requests
      if (response.data.sessionToken) {
        sessionStorage.setItem("sessionToken", response.data.sessionToken);
        axios.defaults.headers.common["x-session-token"] = response.data.sessionToken;
      }

      if (!overrideIdentifier && window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "STORE_CREDENTIALS", identifier: id, password: pwd })
        );
      }

      const user = response.data.user;
      const companyRes = await axios.get(`${path}/api/companies?companyId=${user.CompId}`);

      if (!companyRes.data.success || !companyRes.data.companies?.length) {
        navigate("/registerCompany", { replace: true, state: { pendingUser: user, password: pwd } });
        return;
      }

      const currentLoginCompanyList = companyRes.data.companies;
      const actualSelectedCompany =
        currentLoginCompanyList.find((c) => c.companyId === user.companyId || c.companyId === user.CompId) ||
        currentLoginCompanyList[0];

      const allRes = await axios.get(`${path}/api/All-companies`, { params: { userId: user.UserId } });
      const allCompanies =
        allRes.data.success && allRes.data.data?.length ? allRes.data.data : currentLoginCompanyList;
      const serverIsSuperAdmin = Boolean(allRes.data.isSuperAdmin);

      await login(user, allCompanies, actualSelectedCompany, serverIsSuperAdmin);

      setCompanies(currentLoginCompanyList);
      setSelectedCompany(actualSelectedCompany);

      if (isAutoLogin || currentLoginCompanyList.length === 1) {
        FN_SelectCompany(actualSelectedCompany);
      } else {
        setIsCompanySelectionVisible(true);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Login failed.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FN_SelectCompany = (companyOverride = null) => {
    const company = companyOverride || selectedCompany;
    if (!company) {
      setErrorMessage("Please select a company.");
      return;
    }
    localStorage.setItem("selectedCompanyId", company.CompId);
    const redirectPath = redirectPathFromQuery || "/main/home";
    navigate(redirectPath);
  };

  const handleCapsLockCheck = (e) => {
    if (e.getModifierState) {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  // ── Visual layer (glassmorphism + framer-motion + shadcn-style primitives) ──
  const themeVars = {
    "--background": "#0b0d2a",
    "--foreground": "#f8fafc",
    "--card": "rgba(255,255,255,0.10)",
    "--card-foreground": "#f8fafc",
    "--primary": "#6366f1",
    "--primary-foreground": "#ffffff",
    "--secondary": "rgba(255,255,255,0.10)",
    "--secondary-foreground": "#f8fafc",
    "--muted": "rgba(255,255,255,0.08)",
    "--muted-foreground": "rgba(226,232,240,0.82)",
    "--accent": "rgba(255,255,255,0.14)",
    "--destructive": "#fb7185",
    "--destructive-foreground": "#ffffff",
    "--border": "rgba(255,255,255,0.28)",
    "--input": "rgba(255,255,255,0.26)",
    "--ring": "#22d3ee",
    "--radius": "1.25rem",
  };

  const ease = [0.22, 1, 0.36, 1];
  // Hidden/initial states keep opacity 1 so content is never invisible.
  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduceMotion ? 0 : 0.07, delayChildren: reduceMotion ? 0 : 0.08 } },
  };
  const item = {
    hidden: reduceMotion ? { opacity: 1 } : { opacity: 1, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: reduceMotion ? 0.01 : 0.55, ease } },
  };

  const features = [
    "Multi-company ledgers",
    "GST-ready invoicing",
    "Live cash-flow insight",
  ];

  const capsNotice = (
    <AnimatePresence initial={false}>
      {capsLockOn && (
        <motion.div
          key="caps"
          initial={{ opacity: 1, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: reduceMotion ? 0.01 : 0.2 }}
          style={{ overflow: "hidden" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#fcd34d", fontSize: 12, paddingTop: 6 }}>
            <WarningAmberIcon sx={{ fontSize: 14 }} />Caps Lock is ON
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const errorAlert = (
    <AnimatePresence>
      {errorMessage && (
        <motion.div
          key={errorMessage}
          initial={{ opacity: 1, y: -6 }}
          animate={{ opacity: 1, y: 0, x: reduceMotion ? 0 : [0, -7, 7, -4, 4, 0] }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: reduceMotion ? 0.01 : 0.45 }}
          style={{ marginBottom: 18 }}
        >
          <Alert variant="destructive" className="ld2-alert">
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <WarningAmberIcon sx={{ fontSize: 17, flexShrink: 0 }} />
              {errorMessage}
            </span>
          </Alert>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Input groups use their own initial/animate (not parent variants) so they can never get stuck hidden.
  const fieldMotion = (delay) => ({
    initial: reduceMotion ? false : { opacity: 1, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: reduceMotion ? 0.01 : 0.5, delay: reduceMotion ? 0 : delay, ease },
  });

  const btnMotion = reduceMotion ? {} : { whileHover: { y: -2 }, whileTap: { scale: 0.98 } };

  return (
    <ThemeScope vars={themeVars} className="ld2-root" style={{
      width: "100%", maxWidth: "100vw", overflowX: "hidden", position: "relative",
      display: "flex", flexDirection: "column", alignItems: "center", background: "#0b0d2a",
    }}>
      <style>{[
        "@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');",
        ".ld2-root, .ld2-root * { font-family: 'IBM Plex Sans', -apple-system, 'Segoe UI', Roboto, sans-serif !important; }",
        ".ld2-root { min-height: 100vh; min-height: 100dvh; }",
        ".ld2-root .ld2-input { height: 54px; border-radius: 14px; background: rgba(255,255,255,0.08); color: #f8fafc; border-color: rgba(255,255,255,0.26); padding: 0 16px; font-size: 16px; min-height: 44px; transition: background-color .25s, border-color .25s, box-shadow .25s; }",
        ".ld2-root .ld2-input::placeholder { color: rgba(226,232,240,0.55); }",
        ".ld2-root .ld2-input:hover { border-color: rgba(255,255,255,0.5); }",
        ".ld2-root .ld2-input:focus-visible { background: rgba(255,255,255,0.14); border-color: #22d3ee; box-shadow: 0 0 0 4px rgba(34,211,238,0.2); }",
        ".ld2-root .ld2-input:-webkit-autofill, .ld2-root .ld2-input:-webkit-autofill:hover, .ld2-root .ld2-input:-webkit-autofill:focus { -webkit-box-shadow: 0 0 0 100px #2a2f6b inset; -webkit-text-fill-color: #f8fafc; caret-color: #f8fafc; }",
        ".ld2-root .ld2-label { color: rgba(226,232,240,0.92); font-size: 0.8rem; font-weight: 600; margin-bottom: 8px; letter-spacing: .2px; }",
        ".ld2-root .ld2-primary { width: 100%; height: 3.1rem; border-radius: 14px; font-size: 0.95rem; font-weight: 700; color: #fff; background: linear-gradient(120deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%); background-size: 160% 100%; box-shadow: 0 8px 24px rgba(99,102,241,0.45), inset 0 1px 0 rgba(255,255,255,0.3); transition: background-position .4s ease, box-shadow .2s ease, opacity .15s; }",
        ".ld2-root .ld2-primary:hover { opacity: 1; background-position: 100% 0; box-shadow: 0 12px 30px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.3); }",
        ".ld2-root .ld2-primary:disabled { opacity: .7; }",
        ".ld2-root .ld2-ghost { min-width: 44px; min-height: 44px; color: rgba(226,232,240,0.9); }",
        ".ld2-root .ld2-ghost:hover { background: rgba(255,255,255,0.14); }",
        ".ld2-root .ld2-outline { width: 100%; height: 2.9rem; border-radius: 14px; background: transparent; border-color: rgba(255,255,255,0.35); color: #fff; }",
        ".ld2-root .ld2-outline:hover { background: rgba(255,255,255,0.12); }",
        ".ld2-root .ld2-alert { overflow-wrap: anywhere; word-break: break-word; background: rgba(244,63,94,0.16); border-color: rgba(251,113,133,0.55); color: #ffe4e6; border-radius: 14px; font-size: 0.83rem; }",
        ".ld2-root .ld2-badge { background: rgba(255,255,255,0.09); border-color: rgba(255,255,255,0.18); color: rgba(241,245,249,0.92); font-weight: 600; font-size: 0.7rem; gap: 4px; padding: 4px 10px; }",
        ".ld2-root .ld2-link { display: inline-flex; align-items: center; min-height: 44px; padding: 0 4px; color: #67e8f9; text-decoration: none; font-weight: 600; }",
        ".ld2-root .ld2-link:hover { text-decoration: underline; text-underline-offset: 4px; }",
        ".ld2-root .ld2-link:focus-visible, .ld2-root .ld2-company:focus-visible { outline: 2px solid #67e8f9; outline-offset: 3px; border-radius: 6px; }",
        ".ld2-root .ld2-company { min-width: 0; min-height: 56px; cursor: pointer; display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-radius: 16px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.2); box-shadow: none; transition: background-color .2s, border-color .2s, box-shadow .2s; text-align: left; }",
        ".ld2-root .ld2-company:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.45); }",
        ".ld2-root .ld2-company[aria-checked='true'] { background: rgba(34,211,238,0.14); border-color: #22d3ee; box-shadow: 0 0 0 3px rgba(34,211,238,0.18); }",
        ".ld2-root .ld2-glass { position: relative; overflow: hidden; width: 100%; max-width: 440px; border-radius: 28px; background: linear-gradient(145deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.07) 100%); -webkit-backdrop-filter: blur(26px) saturate(160%); backdrop-filter: blur(26px) saturate(160%); border: 1px solid rgba(255,255,255,0.28); box-shadow: 0 30px 80px rgba(5,6,30,0.5), inset 0 1px 0 rgba(255,255,255,0.4); }",
        "@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) { .ld2-root .ld2-glass { background: rgba(30,33,90,0.9); } }",
        ".ld2-root .ld2-logo { height: 44px; max-width: 100%; }",
        "@media (min-width: 1440px) { .ld2-root .ld2-glass { max-width: 480px; } }",
        "@media (max-width: 380px) { .ld2-root .ld2-glass { border-radius: 22px; } .ld2-root .ld2-badge { font-size: 0.66rem; padding: 3px 8px; } }",
        "@media (max-height: 520px) { .ld2-root .ld2-logo { height: 30px; } .ld2-root .ld2-glass { max-width: 420px; } .ld2-root .ld2-company { padding: 8px 12px; } }",
        ".ld2-dialog .ld2-dev-btn { height: 2.75rem; min-width: 84px; border-radius: 10px; font-size: .75rem; font-weight: 700; background: #e11d48; color: #fff; }",
      ].join("\n")}</style>

      {/* ── Animated mesh gradient background ── */}
      <div aria-hidden="true" style={{ position: "fixed", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        <Blob ax={8} ay={12} amp={0.1} dur={22}
          style={{ position: "absolute", top: "-18%", left: "-12%", width: "min(70vw, 720px)", height: "min(70vw, 720px)", borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #6366f1 0%, rgba(99,102,241,0) 68%)", filter: "blur(40px)", opacity: 0.9 }} />
        <Blob ax={9} ay={8} amp={0.08} dur={26}
          style={{ position: "absolute", bottom: "-22%", right: "-12%", width: "min(75vw, 780px)", height: "min(75vw, 780px)", borderRadius: "50%", background: "radial-gradient(circle at 60% 60%, #06b6d4 0%, rgba(6,182,212,0) 68%)", filter: "blur(44px)", opacity: 0.75 }} />
        <Blob ax={7} ay={10} amp={0.12} dur={30}
          style={{ position: "absolute", top: "25%", right: "10%", width: "min(55vw, 560px)", height: "min(55vw, 560px)", borderRadius: "50%", background: "radial-gradient(circle at 50% 50%, #a855f7 0%, rgba(168,85,247,0) 68%)", filter: "blur(48px)", opacity: 0.7 }} />
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 120%, rgba(11,13,42,0.7) 0%, transparent 60%)" }} />
      </div>

      {/* ── Centered glass card ── */}
      <main style={{ position: "relative", zIndex: 1, width: "100%", flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(16px, 4vw, 40px) 16px" }}>
        <MotionCard
          className="ld2-glass"
          initial={reduceMotion ? false : { opacity: 1, y: 28, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: reduceMotion ? 0.01 : 0.7, ease }}
        >
          {/* Sheen sweep */}
          {!reduceMotion && (
            <motion.div aria-hidden="true"
              initial={{ x: "-120%" }} animate={{ x: "420%" }} transition={{ duration: 2.6, delay: 0.8, ease: "easeInOut" }}
              style={{ position: "absolute", top: 0, left: 0, width: "30%", height: "100%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)", transform: "skewX(-18deg)", pointerEvents: "none", zIndex: 0 }} />
          )}

          <AnimatePresence mode="wait" initial={false}>
            {!isCompanySelectionVisible ? (

              /* ── LOGIN FORM ── */
              <motion.div key="login" variants={container} initial="hidden" animate="show" style={{ position: "relative", zIndex: 1 }}
                exit={{ opacity: 0, y: reduceMotion ? 0 : -12, transition: { duration: reduceMotion ? 0.01 : 0.2 } }}>
                <CardHeader style={{ alignItems: "center", textAlign: "center", padding: "clamp(24px, 5vw, 38px) clamp(20px, 5vw, 38px) 20px" }}>
                  <motion.div variants={item}>
                    <img src="/RBLOGOBLUE.svg" alt="ReonBook" className="ld2-logo" style={{ filter: "brightness(0) invert(1)", opacity: 0.95, display: "block", margin: "0 auto 14px" }} />
                  </motion.div>
                  <motion.div variants={item}>
                    <CardTitle style={{ fontSize: "clamp(1.6rem, 5vw, 1.95rem)", fontWeight: 800, color: "#fff", marginBottom: 8 }}>Welcome</CardTitle>
                    <CardDescription style={{ fontSize: "0.9rem" }}>Sign in to access your ReonBook dashboard</CardDescription>
                  </motion.div>
                </CardHeader>

                <CardContent style={{ padding: "0 clamp(20px, 5vw, 38px) 8px" }}>
                  <form onSubmit={(e) => { e.preventDefault(); setIsAutoLogin(false); FN_HandleLogin(); }}>
                    <motion.div {...fieldMotion(0.12)} style={{ marginBottom: 16 }}>
                      <Label htmlFor="ld2-identifier" className="ld2-label">Username, Email or Mobile</Label>
                      <Input id="ld2-identifier" name="identifier" className="ld2-input" autoComplete="username" placeholder="you@company.com"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)} />
                    </motion.div>

                    <motion.div {...fieldMotion(0.19)} style={{ marginBottom: 6 }}>
                      <Label htmlFor="ld2-password" className="ld2-label">Password</Label>
                      <div style={{ position: "relative" }}>
                        <Input id="ld2-password" name="password" className="ld2-input" autoComplete="current-password" placeholder="Enter your password"
                          type={showPassword ? "text" : "password"} value={userPassword}
                          style={{ paddingRight: 84 }}
                          onChange={(e) => setUserPassword(e.target.value)}
                          onKeyUp={handleCapsLockCheck} onFocus={handleCapsLockCheck} onBlur={() => setCapsLockOn(false)} />
                        <div style={{ position: "absolute", top: 0, bottom: 0, right: 4, display: "flex", alignItems: "center", gap: 2 }}>
                          {capsLockOn && <WarningAmberIcon aria-hidden="true" sx={{ color: "#fcd34d", fontSize: 18 }} />}
                          <Button type="button" variant="ghost" size="icon" className="ld2-ghost" style={{ width: 44, height: 44 }}
                            onClick={handleTogglePasswordVisibility}
                            aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                          </Button>
                        </div>
                      </div>
                      {capsNotice}
                    </motion.div>

                    <motion.div variants={item} style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0 22px" }}>
                      <Link to="/forgot-password" className="ld2-link" style={{ fontSize: "0.82rem" }}>Forgot password?</Link>
                    </motion.div>

                    {errorAlert}

                    <motion.div variants={item}>
                      <MotionButton type="submit" className="ld2-primary" disabled={isLoading} {...btnMotion}>
                        {isLoading
                          ? <span style={{ display: "flex", alignItems: "center", gap: 8 }}><CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />Signing in...</span>
                          : "Sign In"}
                      </MotionButton>
                    </motion.div>

                    {typeof window !== "undefined" && window.isNativeApp && window.ReactNativeWebView && (
                      <motion.div variants={item} style={{ marginTop: 12 }}>
                        <MotionButton type="button" variant="outline" className="ld2-outline" {...btnMotion}
                          onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: "REQUEST_BIOMETRIC" }))}>
                          Login with Biometric
                        </MotionButton>
                      </motion.div>
                    )}
                  </form>

                  <motion.div variants={item} style={{ margin: "24px 0 18px" }}>
                    <Separator style={{ background: "rgba(255,255,255,0.18)" }} />
                  </motion.div>

                  <motion.p variants={item} style={{ textAlign: "center", margin: 0, color: "rgba(226,232,240,0.88)", fontSize: "0.875rem" }}>
                    Don't have an account?{" "}
                    <Link to="/register" className="ld2-link" style={{ fontWeight: 700 }}>Sign up now</Link>
                  </motion.p>
                </CardContent>

                <CardFooter style={{ justifyContent: "center", flexWrap: "wrap", gap: 8, padding: "20px clamp(20px, 5vw, 38px) clamp(24px, 5vw, 34px)" }}>
                  {features.map((feat) => (
                    <motion.span key={feat} variants={item} style={{ display: "inline-flex" }}>
                      <Badge variant="outline" className="ld2-badge">
                        <CheckIcon sx={{ fontSize: 12, color: "#67e8f9" }} />{feat}
                      </Badge>
                    </motion.span>
                  ))}
                </CardFooter>
              </motion.div>

            ) : (

              /* ── COMPANY SELECTION ── */
              <motion.div key="company" variants={container} initial="hidden" animate="show" style={{ position: "relative", zIndex: 1 }}
                exit={{ opacity: 0, y: reduceMotion ? 0 : -12, transition: { duration: reduceMotion ? 0.01 : 0.2 } }}>
                <CardHeader style={{ alignItems: "center", textAlign: "center", padding: "clamp(24px, 5vw, 38px) clamp(20px, 5vw, 38px) 20px" }}>
                  <motion.div variants={item}>
                    <img src="/RBLOGOBLUE.svg" alt="ReonBook" className="ld2-logo" style={{ filter: "brightness(0) invert(1)", opacity: 0.95, display: "block", margin: "0 auto 14px" }} />
                  </motion.div>
                  <motion.div variants={item}>
                    <CardTitle style={{ fontSize: "1.6rem", fontWeight: 800, color: "#fff", marginBottom: 8 }}>Select Organization</CardTitle>
                    <CardDescription style={{ fontSize: "0.9rem" }}>Choose which organization to access</CardDescription>
                  </motion.div>
                </CardHeader>

                <CardContent style={{ padding: "0 clamp(20px, 5vw, 38px) 8px" }}>
                  <div role="radiogroup" aria-label="Choose your organization"
                    style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20, maxHeight: "40dvh", overflowY: "auto", padding: 4 }}>
                    {companies.map((c) => {
                      const active = selectedCompany?.companyId === c.companyId;
                      const pick = () => setSelectedCompany(companies.find((x) => x.companyId === c.companyId));
                      return (
                        <MotionCard key={c.companyId} role="radio" aria-checked={active} tabIndex={0}
                          className="ld2-company" variants={item}
                          {...(reduceMotion ? {} : { whileHover: { y: -2 }, whileTap: { scale: 0.985 } })}
                          onClick={pick}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } }}>
                          <span style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(165,180,252,0.2)", color: "#fff", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            {String(c.companyName || "?").trim().charAt(0).toUpperCase()}
                          </span>
                          <span style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: "0.92rem", color: "#f8fafc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", wordBreak: "break-word" }}>
                            {c.companyName}
                          </span>
                          <AnimatePresence initial={false}>
                            {active && (
                              <motion.span key="tick" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                                style={{ width: 22, height: 22, borderRadius: "50%", background: "#22d3ee", color: "#0b0d2a", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <CheckIcon sx={{ fontSize: 14 }} />
                              </motion.span>
                            )}
                          </AnimatePresence>
                        </MotionCard>
                      );
                    })}
                  </div>

                  {errorAlert}

                  <motion.div variants={item}>
                    <MotionButton type="button" className="ld2-primary" onClick={() => FN_SelectCompany()} {...btnMotion}>
                      Continue to Dashboard →
                    </MotionButton>
                  </motion.div>
                </CardContent>
                <CardFooter style={{ padding: "0 0 clamp(20px, 4vw, 30px)" }} />
              </motion.div>
            )}
          </AnimatePresence>
        </MotionCard>
      </main>

      <p style={{ position: "relative", zIndex: 1, textAlign: "center", margin: 0, padding: "0 16px 20px", color: "rgba(226,232,240,0.72)", fontSize: "0.73rem" }}>
        <CopyrightIcon sx={{ fontSize: 12, verticalAlign: "middle", mr: 0.4 }} />
        {new Date().getFullYear()} Powered by{" "}
        Soltrack Technologies Pvt Ltd
      </p>

      {/* ── SESSION LIMIT PICKER MODAL ── */}
      <Dialog
        className="ld2-root ld2-dialog"
        open={!!sessionPickerData}
        onClose={() => setSessionPickerData(null)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={reduceMotion ? 0 : { enter: 320, exit: 180 }}
        BackdropProps={{ sx: { backdropFilter: "blur(6px)", bgcolor: "rgba(8,9,35,0.6)" } }}
        PaperProps={{ sx: {
          borderRadius: "24px", overflow: "hidden", m: 2,
          background: "linear-gradient(145deg, rgba(55,58,140,0.94) 0%, rgba(30,33,90,0.96) 100%)",
          backdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.25)", color: "#f8fafc",
        } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ThemeScope vars={themeVars} style={{ background: "transparent" }}>
            {/* Top banner */}
            <div style={{ background: "linear-gradient(135deg, rgba(99,102,241,0.55) 0%, rgba(6,182,212,0.4) 100%)", padding: "24px 24px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DevicesIcon sx={{ color: "#fff", fontSize: 22 }} />
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "#fff", lineHeight: 1.3 }}>Session Limit Reached</div>
                  <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.8)", marginTop: 2 }}>
                    {sessionPickerData?.maxSessions} of {sessionPickerData?.maxSessions} allowed session{sessionPickerData?.maxSessions !== 1 ? "s" : ""} in use
                  </div>
                </div>
              </div>
              <div style={{ background: "rgba(255,255,255,0.14)", borderRadius: 12, padding: "8px 16px", fontSize: "0.8rem", color: "rgba(255,255,255,0.92)", lineHeight: 1.5 }}>
                Sign out of one of the devices below to log in on this device.
              </div>
            </div>

            {/* Device list */}
            <div style={{ padding: "16px 20px 8px" }}>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "rgba(203,213,225,0.85)", letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 12 }}>
                Active Sessions
              </div>

              {sessionPickerData?.activeSessions?.map((session, idx) => (
                <motion.div key={session.id}
                  initial={reduceMotion ? false : { opacity: 1, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: reduceMotion ? 0.01 : 0.45, delay: reduceMotion ? 0 : 0.1 + idx * 0.07, ease }}
                  {...(reduceMotion ? {} : { whileHover: { y: -1 } })}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, marginBottom: 8, borderRadius: 16, border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.07)" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(165,180,252,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {deviceIcon(session.device_type)}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: "0.87rem", color: "#f8fafc", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {session.device_name || "Unknown Device"}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2, minWidth: 0 }}>
                      <AccessTimeIcon sx={{ fontSize: 12, color: "rgba(203,213,225,0.8)" }} />
                      <span style={{ fontSize: "0.73rem", color: "rgba(203,213,225,0.85)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {timeAgo(session.last_activity)}
                        {session.ip_address ? ` · ${session.ip_address}` : ""}
                      </span>
                    </div>
                  </div>

                  <Button
                    size="sm"
                    variant="destructive"
                    className="ld2-dev-btn"
                    disabled={kickingSessionId === session.id}
                    onClick={async () => {
                      setKickingSessionId(session.id);
                      setSessionPickerData(null);
                      await FN_HandleLogin(identifier, userPassword, session.id);
                      setKickingSessionId(null);
                    }}
                  >
                    {kickingSessionId === session.id
                      ? <CircularProgress size={13} sx={{ color: "#fff" }} />
                      : "Sign Out"}
                  </Button>
                </motion.div>
              ))}
            </div>

            {/* Cancel */}
            <div style={{ padding: "4px 20px 20px" }}>
              <Button variant="outline" className="ld2-outline" style={{ height: "2.75rem", color: "#e2e8f0" }}
                onClick={() => setSessionPickerData(null)}>
                Cancel
              </Button>
            </div>
          </ThemeScope>
        </DialogContent>
      </Dialog>

    </ThemeScope>
  );
}

export default LoginDesign02;
